import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Eye, Download, Filter, Calendar, CreditCard, Package, User, DollarSign, ArrowUpDown, RefreshCw, ShoppingCart } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import Pagination from '../components/Pagination';
import { GetAllOrder } from '../components/Api.jsx';
// Removed PrimeReact imports - using standard HTML/Tailwind
import axios from 'axios';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState(''); // State for global filter
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [tempStatusFilter, setTempStatusFilter] = useState('all');
  const [tempPaymentStatusFilter, setTempPaymentStatusFilter] = useState('all');
  const [tempDateFilter, setTempDateFilter] = useState('all');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    global: { value: null },
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6); // Changed default to 5
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [refreshing, setRefreshing] = useState(false);

  const fetchOrders = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }

      const result = await GetAllOrder();
      console.log(result.data);
      // Map orders to include all available fields
      const formattedOrders = result.data.map(order => ({
        ...order,
        email: order.user?.email || '-',
        firstName: order.user?.firstName || '-',
        lastName: order.user?.lastName || '-',
        firstName_lastName: `${order.user?.firstName || ''}_${order.user?.lastName || ''}`,
        mobileNumber: order.user?.mobileNumber || '-',
        totalPrice: order.totalPrice || 0,
        orderNumber: order.orderNumber || `ORD-${order.orderId}`,
        paymentStatus: order.paymentStatus || 'PENDING',
        orderStatus: order.orderStatus || 'PENDING',
        currency: order.currency || 'INR',
        paymentMethod: order.paymentMethod || '-',
        paymentReference: order.paymentReference || '-',
        productType: order.productType || 'Subscription',
        notes: order.notes || '',
        createdDate: order.createdAt || order.createdDate,
        updatedDate: order.updatedAt || order.updatedDate,
      }));
      // Sort orders so the latest (most recent createdDate) come first
      formattedOrders.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
      setOrders(formattedOrders);
      setTotalPages(Math.ceil(formattedOrders.length / itemsPerPage));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleRefresh = () => {
    fetchOrders(true);
  };

  // Function to compute firstName_lastName
  const getFullNameWithUnderscore = (rowData) => `${rowData.user?.firstName || ''}_${rowData.user?.lastName || ''}`.trim() || '-';

  // Status badge components
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      'PAID': { color: 'bg-green-100 text-green-800', icon: '✅' },
      'FAILED': { color: 'bg-red-100 text-red-800', icon: '❌' },
      'REFUNDED': { color: 'bg-blue-100 text-blue-800', icon: '↩️' },
      'PARTIALLY_REFUNDED': { color: 'bg-orange-100 text-orange-800', icon: '🔄' }
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {status}
      </span>
    );
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      'CONFIRMED': { color: 'bg-blue-100 text-blue-800', icon: '✅' },
      'PROCESSING': { color: 'bg-purple-100 text-purple-800', icon: '⚙️' },
      'SHIPPED': { color: 'bg-indigo-100 text-indigo-800', icon: '📦' },
      'DELIVERED': { color: 'bg-green-100 text-green-800', icon: '🎉' },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: '❌' },
      'RETURNED': { color: 'bg-orange-100 text-orange-800', icon: '↩️' }
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {status}
      </span>
    );
  };

  // Date formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Open order details modal
  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Close order details modal
  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  // Export orders to CSV
  const exportToCSV = () => {
    const csvData = filteredOrders.map(order => ({
      'Order ID': order.orderId,
      'Order Number': order.orderNumber || `ORD-${order.orderId}`,
      'Customer Name': `${order.firstName} ${order.lastName}`,
      'Email': order.email,
      'Mobile': order.mobileNumber,
      'Product Type': order.productType || 'Subscription',
      'Product ID': order.productId,
      'Amount': `${order.currency} ${order.totalPrice?.toFixed(2) || '0.00'}`,
      'Payment Status': order.paymentStatus,
      'Order Status': order.orderStatus,
      'Payment Method': order.paymentMethod || '-',
      'Payment Reference': order.paymentReference || '-',
      'Created Date': formatDate(order.createdDate),
      'Updated Date': formatDate(order.updatedDate),
      'Notes': order.notes || ''
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter orders based on search term and filters with sorting
  const filteredOrders = (() => {
    let filtered = orders.filter(order => {
      // Global search filter
      if (globalFilterValue) {
        const searchTerm = globalFilterValue.toLowerCase();
        const matchesSearch = (
          (order.email && order.email.toLowerCase().includes(searchTerm)) ||
          (order.mobileNumber && order.mobileNumber.includes(searchTerm)) ||
          (order.totalPrice && order.totalPrice.toString().includes(searchTerm)) ||
          (order.orderNumber && order.orderNumber.toLowerCase().includes(searchTerm)) ||
          (getFullNameWithUnderscore(order).toLowerCase().includes(searchTerm))
        );
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && order.orderStatus !== statusFilter) {
        return false;
      }

      // Payment status filter
      if (paymentStatusFilter !== 'all' && order.paymentStatus !== paymentStatusFilter) {
        return false;
      }

      // Date filter
      if (dateFilter !== 'all') {
        const orderDate = new Date(order.createdDate);
        const now = new Date();
        const daysDiff = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case 'today':
            if (daysDiff !== 0) return false;
            break;
          case 'week':
            if (daysDiff > 7) return false;
            break;
          case 'month':
            if (daysDiff > 30) return false;
            break;
          case 'year':
            if (daysDiff > 365) return false;
            break;
        }
      }

      return true;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        // Handle special cases for nested properties
        if (sortConfig.key === 'customer') {
          aValue = getFullNameWithUnderscore(a).toLowerCase();
          bValue = getFullNameWithUnderscore(b).toLowerCase();
        } else if (sortConfig.key === 'amount') {
          aValue = a.totalPrice;
          bValue = b.totalPrice;
        } else if (sortConfig.key === 'created') {
          aValue = new Date(a.createdDate);
          bValue = new Date(b.createdDate);
        } else if (sortConfig.key === 'paymentStatus') {
          aValue = a.paymentStatus;
          bValue = b.paymentStatus;
        } else if (sortConfig.key === 'orderStatus') {
          aValue = a.orderStatus;
          bValue = b.orderStatus;
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Convert to string for comparison if needed
        if (typeof aValue !== 'string') aValue = String(aValue);
        if (typeof bValue !== 'string') bValue = String(bValue);

        // Case insensitive comparison
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  })();

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    setTotalPages(Math.ceil(filteredOrders.length / newItemsPerPage));
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredOrders.length / itemsPerPage));
  }, [filteredOrders.length, itemsPerPage]);

  return (
    <div className="text-black min-h-screen bg-white p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Manage and track user's orders. Monitor payment status, order fulfillment, and customer transaction history.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh orders data"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center gap-4">
          {/* Search */}
          <div className="relative flex-grow max-w-md w-full">
            <input
              type="text"
              value={globalFilterValue}
              onChange={(e) => {
                setGlobalFilterValue(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              placeholder="Search orders..."
              className="w-full px-4 py-2 pl-10 pr-4 text-black border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0 relative">
            <button
              onClick={() => {
                setTempStatusFilter(statusFilter);
                setTempPaymentStatusFilter(paymentStatusFilter);
                setTempDateFilter(dateFilter);
                setIsFilterModalOpen(!isFilterModalOpen);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 font-medium whitespace-nowrap"
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
              {(statusFilter !== 'all' || paymentStatusFilter !== 'all' || dateFilter !== 'all') && (
                <span className="flex h-2 w-2 relative ml-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
              )}
            </button>

            {/* Filter Dropdown */}
            {isFilterModalOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl w-72 overflow-hidden z-50 border border-gray-200">
                <div className="p-4 space-y-4">
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-700 mb-1">Order Status</label>
                    <select
                      value={tempStatusFilter}
                      onChange={(e) => setTempStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="all">All Order Status</option>
                      <option value="PENDING">Pending</option>
                      <option value="CONFIRMED">Confirmed</option>
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-700 mb-1">Payment Status</label>
                    <select
                      value={tempPaymentStatusFilter}
                      onChange={(e) => setTempPaymentStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="all">All Payment Status</option>
                      <option value="PENDING">Pending</option>
                      <option value="PAID">Paid</option>
                      <option value="FAILED">Failed</option>
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-700 mb-1">Time Period</label>
                    <select
                      value={tempDateFilter}
                      onChange={(e) => setTempDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="year">This Year</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end p-3 border-t bg-gray-50 space-x-2">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setPaymentStatusFilter('all');
                      setDateFilter('all');
                      setCurrentPage(1);
                      setIsFilterModalOpen(false);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter(tempStatusFilter);
                      setPaymentStatusFilter(tempPaymentStatusFilter);
                      setDateFilter(tempDateFilter);
                      setCurrentPage(1);
                      setIsFilterModalOpen(false);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 font-medium whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('orderNumber')}
                >
                  <div className="flex items-center">
                    Order Details
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('orderNumber')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('customer')}
                >
                  <div className="flex items-center">
                    Customer
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('customer')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('paymentStatus')}
                >
                  <div className="flex items-center">
                    Payment Status
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('paymentStatus')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('orderStatus')}
                >
                  <div className="flex items-center">
                    Order Status
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('orderStatus')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center">
                    Amount
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('amount')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center">
                    Date
                    <ArrowUpDown className="ml-1 w-4 h-4" />
                    <span className="ml-1">{getSortIndicator('created')}</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Package className="w-12 h-12 text-gray-400 mb-2" />
                      <p className="text-lg font-medium">{orders.length === 0 ? 'No orders found' : 'No orders match your search'}</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search terms</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentOrders.map((order, index) => (
                  <tr key={order.orderId || index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-500 font-mono">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    {/* Order Details */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">
                          {order.orderNumber || `ORD-${order.orderId}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {order.orderId}
                        </div>
                        <div className="text-xs text-gray-500">
                          Product: {order.productType || 'Subscription'}
                        </div>
                      </div>
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">
                          {order.firstName} {order.lastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.mobileNumber}
                        </div>
                      </div>
                    </td>

                    {/* Payment Status */}
                    <td className="px-4 py-4">
                      {getPaymentStatusBadge(order.paymentStatus)}
                    </td>

                    {/* Order Status */}
                    <td className="px-4 py-4">
                      {getOrderStatusBadge(order.orderStatus)}
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">
                          {order.currency} {order.totalPrice?.toFixed(2) || '0.00'}
                        </div>
                        {order.paymentMethod && order.paymentMethod !== '-' && (
                          <div className="text-xs text-gray-500">
                            via {order.paymentMethod}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-900">
                          {formatDate(order.createdDate)}
                        </div>
                        {order.updatedDate && order.updatedDate !== order.createdDate && (
                          <div className="text-xs text-gray-500">
                            Updated: {formatDate(order.updatedDate)}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openOrderModal(order)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalElements={filteredOrders.length}
        pageSize={itemsPerPage}
        setPageSize={setItemsPerPage}
        itemName="Orders"
      />

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                  <p className="text-gray-600">Order #{selectedOrder.orderNumber || `ORD-${selectedOrder.orderId}`}</p>
                </div>
                <button
                  onClick={closeOrderModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Order Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Order Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Order Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-medium">{selectedOrder.orderId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Number:</span>
                      <span className="font-medium">{selectedOrder.orderNumber || `ORD-${selectedOrder.orderId}`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Product Type:</span>
                      <span className="font-medium">{selectedOrder.productType || 'Subscription'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Product ID:</span>
                      <span className="font-medium">{selectedOrder.productId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="font-medium">{formatDate(selectedOrder.createdDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Updated:</span>
                      <span className="font-medium">{formatDate(selectedOrder.updatedDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">{selectedOrder.firstName} {selectedOrder.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{selectedOrder.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mobile:</span>
                      <span className="font-medium">{selectedOrder.mobileNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">User ID:</span>
                      <span className="font-medium text-xs">{selectedOrder.userId}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payment Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-medium">{selectedOrder.currency} {selectedOrder.totalPrice?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Status:</span>
                      <div>{getPaymentStatusBadge(selectedOrder.paymentStatus)}</div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="font-medium">{selectedOrder.paymentMethod || '-'}</span>
                    </div>
                    {selectedOrder.paymentReference && selectedOrder.paymentReference !== '-' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reference:</span>
                        <span className="font-medium text-xs">{selectedOrder.paymentReference}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Status */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Order Status
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <div>{getOrderStatusBadge(selectedOrder.orderStatus)}</div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Currency:</span>
                      <span className="font-medium">{selectedOrder.currency}</span>
                    </div>
                    {selectedOrder.notes && (
                      <div className="mt-3">
                        <span className="text-gray-600 block mb-1">Notes:</span>
                        <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                          {selectedOrder.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={closeOrderModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                {/* <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Update Status
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}